import { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { ScanLine, CheckCircle, AlertTriangle, Upload, X, Camera } from "lucide-react";
import QRScanner from "@/components/common/QRScanner";

const CONDITIONS = [
  { value: "RECEIVED_OK", label: "Zustand OK", icon: CheckCircle, color: "text-green-700 border-green-500" },
  { value: "DAMAGED", label: "Beschädigt", icon: AlertTriangle, color: "text-red-700 border-red-500" },
];

/**
 * Reusable Rescue-Bag Intake panel.
 * - `initialQr` (optional): pre-fill and auto-lookup on mount.
 * - `onSuccess` (optional): called after a successful intake save.
 */
export default function RescueBagIntakePanel({ initialQr = "", onSuccess }) {
  const [qr, setQr] = useState(initialQr);
  const [bagInfo, setBagInfo] = useState(null);
  const [condition, setCondition] = useState("");
  const [photoUrls, setPhotoUrls] = useState([]);
  const [damageReason, setDamageReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState({ kind: "", text: "" });
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const autoDoneRef = useRef(false);
  const [qrScannerOpen, setQrScannerOpen] = useState(false);

  const lookupWith = useCallback(async (raw) => {
    setMessage({ kind: "", text: "" }); setBagInfo(null);
    const val = (raw || "").trim();
    if (val.length < 8) { setMessage({ kind: "error", text: "QR-Code zu kurz." }); return; }
    setBusy(true);
    try {
      const { data } = await api.get(`/rescue/warehouse/bag-lookup?qr=${encodeURIComponent(val)}`);
      setBagInfo(data);
      setCondition(""); setPhotoUrls([]); setDamageReason(""); setNote("");
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      setMessage({ kind: "error", text: code === "BAG_NOT_FOUND" ? "Kein Bag mit diesem Code gefunden." : code === "BAG_NOT_ACTIVATED" ? "Bag ist nicht aktiviert." : (code || "Fehler beim Nachschlagen.") });
    } finally { setBusy(false); }
  }, []);

  // Auto-lookup once when initialQr is provided
  useEffect(() => {
    if (!autoDoneRef.current && initialQr && initialQr.trim().length >= 8) {
      autoDoneRef.current = true;
      lookupWith(initialQr);
    }
  }, [initialQr, lookupWith]);

  const uploadPhoto = async (file) => {
    if (photoUrls.length >= 3) { setMessage({ kind: "error", text: "Maximal 3 Fotos." }); return; }
    const form = new FormData(); form.append("file", file);
    try {
      const { data } = await api.post("/upload?area_scope=WAREHOUSE", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoUrls(prev => [...prev, data.url]);
    } catch (_) {
      setMessage({ kind: "error", text: "Upload fehlgeschlagen." });
    }
  };

  const submit = async () => {
    if (!bagInfo?.bag) return;
    if (!condition) { setMessage({ kind: "error", text: "Bitte Zustand wählen." }); return; }
    if (photoUrls.length < 1) { setMessage({ kind: "error", text: "Mindestens 1 Foto erforderlich." }); return; }
    if (condition === "DAMAGED" && damageReason.trim().length < 3) { setMessage({ kind: "error", text: "Grund für Beschädigung angeben." }); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/rescue/warehouse/intake", {
        qr_code_value: bagInfo.bag.qr_code_value,
        condition, photo_urls: photoUrls,
        damage_reason: condition === "DAMAGED" ? damageReason : null,
        note: note || null,
      });
      const successText = condition === "DAMAGED"
        ? `Beschädigten-Case eröffnet. Der Verkäufer wurde benachrichtigt (${data.damaged_case?.damaged_case_id?.slice(0,8).toUpperCase()}).`
        : "Intake gespeichert. Bag ist eingegangen.";
      setMessage({ kind: "success", text: successText });
      setBagInfo(null); setQr(""); setCondition(""); setPhotoUrls([]); setDamageReason(""); setNote("");
      if (onSuccess) onSuccess({ condition, response: data });
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      setMessage({ kind: "error", text: code || "Fehler beim Speichern." });
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="rescue-bag-intake-panel" className="space-y-4">
      {/* QR Scan input */}
      <div>
        <label className="text-[10px] text-[#999] uppercase tracking-wider font-medium block mb-1">
          Rescue-Bag QR-Code scannen oder Bag-ID eingeben
        </label>
        <div className="flex gap-2">
          <div className="flex-1 flex items-center gap-2 px-3 py-2 border border-[#0A0A0A] rounded">
            <ScanLine size={16} className="text-[#666] shrink-0" />
            <input
              data-testid="rescue-intake-qr-input"
              type="text"
              value={qr}
              onChange={(e) => setQr(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") lookupWith(qr); }}
              placeholder="Voller QR-Wert oder 8-stellige Bag-ID"
              className="flex-1 text-sm bg-transparent outline-none font-mono"
            />
          </div>
          <button data-testid="rescue-intake-scan-camera"
            onClick={() => setQrScannerOpen(true)}
            title="Kamera-Scanner öffnen"
            className="px-3 py-2 text-xs font-medium border border-[#0A0A0A] rounded flex items-center gap-1">
            <Camera size={14} /> Scan
          </button>
          <button data-testid="rescue-intake-lookup" onClick={() => lookupWith(qr)} disabled={busy}
            className="px-4 py-2 text-xs font-medium bg-[#0A0A0A] text-white rounded disabled:opacity-50">
            Nachschlagen
          </button>
        </div>
        <p className="text-[10px] text-[#999] mt-1">Tipp: Die 8-stellige Bag-ID steht direkt unter dem QR-Code auf dem Aufkleber.</p>
      </div>

      {message.text && (
        <div data-testid="rescue-intake-msg" className={`p-2 text-xs rounded ${message.kind === "error" ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`}>
          {message.text}
        </div>
      )}

      {bagInfo?.bag && (
        <div data-testid="rescue-intake-bag-info" className="p-3 border border-[#E5E5E5] rounded space-y-2">
          <div className="flex items-center gap-2">
            <p className="text-xs font-medium text-[#0A0A0A]">Bag {bagInfo.bag.bag_id.slice(0,8).toUpperCase()}</p>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${bagInfo.bag.status === "ACTIVATED" ? "bg-blue-50 text-blue-700" : "bg-[#F5F5F5] text-[#666]"}`}>{bagInfo.bag.status}</span>
          </div>
          {bagInfo.activation && (
            <p className="text-[10px] text-[#666]">
              Kategorie: <b>{bagInfo.activation.category}</b> · Tragedauer: <b>{bagInfo.activation.wear_duration_days} Tage</b> · Aktiviert am {bagInfo.activation.activated_at?.slice(0,10)}
            </p>
          )}
          {bagInfo.seller?.email && (
            <p className="text-[10px] text-[#999]">
              Seller: {bagInfo.seller.display_name ? `${bagInfo.seller.display_name} ` : ""}({bagInfo.seller.email})
            </p>
          )}

          <div className="pt-2 border-t border-[#F0F0F0]">
            <label className="text-[10px] text-[#999] uppercase tracking-wider font-medium block mb-1">Zustand</label>
            <div className="grid grid-cols-2 gap-2">
              {CONDITIONS.map(c => (
                <button
                  key={c.value}
                  data-testid={`rescue-intake-cond-${c.value}`}
                  onClick={() => setCondition(c.value)}
                  className={`flex items-center gap-1.5 justify-center px-3 py-2 text-xs font-medium border rounded ${condition === c.value ? c.color + " bg-[#FAFAFA]" : "text-[#666] border-[#E5E5E5]"}`}
                >
                  <c.icon size={14} /> {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] text-[#999] uppercase tracking-wider font-medium block mb-1">Fotos (1–3)</label>
            <div className="flex gap-2 flex-wrap">
              {photoUrls.map((u, i) => (
                <div key={u} data-testid={`rescue-intake-photo-${i}`} className="relative w-16 h-16 bg-[#F5F5F5] overflow-hidden rounded">
                  <img src={u} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setPhotoUrls(prev => prev.filter((_, ix) => ix !== i))}
                    className="absolute top-0 right-0 bg-white/80 p-0.5">
                    <X size={10} />
                  </button>
                </div>
              ))}
              {photoUrls.length < 3 && (
                <>
                  <button
                    data-testid="rescue-intake-photo-add"
                    onClick={() => fileInputRef.current?.click()}
                    title="Aus Galerie wählen"
                    className="w-16 h-16 border-2 border-dashed border-[#CCC] rounded flex items-center justify-center text-[#666]">
                    <Upload size={16} />
                  </button>
                  <button
                    data-testid="rescue-intake-photo-camera"
                    onClick={() => cameraInputRef.current?.click()}
                    title="Kamera öffnen"
                    className="w-16 h-16 border-2 border-dashed border-[#CCC] rounded flex items-center justify-center text-[#666]">
                    <Camera size={16} />
                  </button>
                </>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" hidden
              onChange={(e) => { if (e.target.files?.[0]) uploadPhoto(e.target.files[0]); e.target.value = ""; }} />
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" hidden
              onChange={(e) => { if (e.target.files?.[0]) uploadPhoto(e.target.files[0]); e.target.value = ""; }} />
          </div>

          {condition === "DAMAGED" && (
            <div>
              <label className="text-[10px] text-[#999] uppercase tracking-wider font-medium block mb-1">Grund der Beschädigung</label>
              <input
                data-testid="rescue-intake-damage-reason"
                type="text"
                value={damageReason}
                onChange={(e) => setDamageReason(e.target.value)}
                placeholder="z. B. Falscher Inhalt, offen, verschmutzt …"
                className="w-full px-3 py-2 text-xs border border-[#E5E5E5] rounded"
              />
            </div>
          )}

          <div>
            <label className="text-[10px] text-[#999] uppercase tracking-wider font-medium block mb-1">Notiz (optional)</label>
            <input
              data-testid="rescue-intake-note"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-[#E5E5E5] rounded"
            />
          </div>

          <button
            data-testid="rescue-intake-submit"
            onClick={submit}
            disabled={busy || !condition || photoUrls.length === 0}
            className="w-full px-3 py-2 text-xs font-medium bg-[#0A0A0A] text-white rounded disabled:opacity-50">
            Intake speichern
          </button>
        </div>
      )}
      <QRScanner
        open={qrScannerOpen}
        onScan={(val) => { setQrScannerOpen(false); setQr(val); lookupWith(val); }}
        onClose={() => setQrScannerOpen(false)}
      />
    </div>
  );
}
